import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  HardHat,
  Loader2,
  MapPin,
  Settings2,
  Users,
} from "lucide-react";
import {
  BETA_CURRENCY_OPTIONS,
  BETA_PAY_PERIOD_OPTIONS,
  BETA_PAY_RULE_TEMPLATE_LABELS,
  BETA_SETUP_PATH_LABELS,
  DEFAULT_BETA_GEOFENCE_RADIUS_METERS,
  finishBetaOnboardingSchema,
  type BetaPayRuleTemplate,
  type BetaSetupPath,
  type FinishBetaOnboardingInput,
} from "@crewledger/shared/workspace";
import { useAuth } from "@/features/auth/auth-provider";
import { finishBetaOnboarding } from "@/features/beta-onboarding/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InfoBanner } from "@/components/design/info-banner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const TIMEZONE_OPTIONS = [
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/New_York", label: "Eastern (New York)" },
];

const STEPS = [
  { id: 1, title: "Create workspace", icon: Building2 },
  { id: 2, title: "First job site", icon: MapPin },
  { id: 3, title: "Setup path", icon: Users },
  { id: 4, title: "Starter pay rules", icon: Settings2 },
  { id: 5, title: "Finish", icon: CheckCircle2 },
] as const;

const PAY_RULE_OPTIONS: Array<{
  value: BetaPayRuleTemplate;
  label: string;
  description: string;
}> = [
  {
    value: "weekly_40h",
    label: BETA_PAY_RULE_TEMPLATE_LABELS.weekly_40h,
    description: "1.5× pay after 40 hours in the work week.",
  },
  {
    value: "daily_ot",
    label: BETA_PAY_RULE_TEMPLATE_LABELS.daily_ot,
    description: "1.5× after 8 hours/day, 2× after 12 hours/day.",
  },
  {
    value: "weekend_holiday",
    label: BETA_PAY_RULE_TEMPLATE_LABELS.weekend_holiday,
    description: "Higher multipliers on weekends and holidays.",
  },
  {
    value: "later",
    label: "I'll configure later",
    description: "Skip pay rules for now — set them up in Settings.",
  },
];

const SETUP_PATH_OPTIONS: Array<{
  value: BetaSetupPath;
  label: string;
  description: string;
}> = [
  {
    value: "blank",
    label: BETA_SETUP_PATH_LABELS.blank,
    description: "Open an empty workspace and add workers when you're ready.",
  },
  {
    value: "import_csv",
    label: BETA_SETUP_PATH_LABELS.import_csv,
    description: "Upload a CSV roster after setup.",
  },
  {
    value: "add_manual",
    label: BETA_SETUP_PATH_LABELS.add_manual,
    description: "Add your first workers one by one.",
  },
];

function slugifySiteCode(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

export function BetaOnboardingWizard() {
  const navigate = useNavigate();
  const { refreshSession, workspace } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [companyName, setCompanyName] = useState(workspace?.companyName ?? "");
  const [timezone, setTimezone] = useState(
    import.meta.env.VITE_DEFAULT_TIMEZONE ?? "America/Los_Angeles",
  );
  const [defaultCurrency, setDefaultCurrency] = useState<"USD" | "CAD" | "EUR">("USD");
  const [payPeriodType, setPayPeriodType] = useState<
    "WEEKLY" | "BIWEEKLY" | "SEMIMONTHLY" | "MONTHLY"
  >("BIWEEKLY");

  const [skipSite, setSkipSite] = useState(false);
  const [siteName, setSiteName] = useState("");
  const [siteCode, setSiteCode] = useState("");
  const [siteAddress, setSiteAddress] = useState("");
  const [geofenceEnabled, setGeofenceEnabled] = useState(false);
  const [geofenceRadiusMeters, setGeofenceRadiusMeters] = useState(
    DEFAULT_BETA_GEOFENCE_RADIUS_METERS,
  );

  const [setupPath, setSetupPath] = useState<BetaSetupPath>("blank");
  const [payRuleTemplate, setPayRuleTemplate] = useState<BetaPayRuleTemplate>("weekly_40h");

  const payload = useMemo<FinishBetaOnboardingInput>(
    () => ({
      companyName: companyName.trim(),
      timezone,
      defaultCurrency,
      payPeriodType,
      site: skipSite
        ? {
            skip: true,
            geofenceEnabled: false,
            geofenceRadiusMeters: DEFAULT_BETA_GEOFENCE_RADIUS_METERS,
          }
        : {
            skip: false,
            name: siteName.trim(),
            code: siteCode.trim() || slugifySiteCode(siteName),
            address: siteAddress.trim() || undefined,
            geofenceEnabled,
            geofenceRadiusMeters,
          },
      setupPath,
      payRuleTemplate,
    }),
    [
      companyName,
      timezone,
      defaultCurrency,
      payPeriodType,
      skipSite,
      siteName,
      siteCode,
      siteAddress,
      geofenceEnabled,
      geofenceRadiusMeters,
      setupPath,
      payRuleTemplate,
    ],
  );

  function validateCurrentStep(): string | null {
    if (step === 1) {
      if (companyName.trim().length < 2) return "Company name must be at least 2 characters.";
      return null;
    }
    if (step === 2 && !skipSite) {
      if (!siteName.trim()) return "Site name is required, or skip this step.";
      if (!siteCode.trim() && !slugifySiteCode(siteName)) {
        return "Site code is required.";
      }
      return null;
    }
    return null;
  }

  function goNext(): void {
    const stepError = validateCurrentStep();
    if (stepError) {
      setError(stepError);
      return;
    }
    setError(null);
    setStep((current) => Math.min(current + 1, STEPS.length));
  }

  function goBack(): void {
    setError(null);
    setStep((current) => Math.max(current - 1, 1));
  }

  async function handleFinish(): Promise<void> {
    setSubmitting(true);
    setError(null);

    const parsed = finishBetaOnboardingSchema.safeParse(payload);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Please check the form and try again.");
      setSubmitting(false);
      return;
    }

    try {
      await finishBetaOnboarding(parsed.data);
      await refreshSession();
      navigate("/dashboard/setup", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not finish setup.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <HardHat className="h-6 w-6" />
          </div>
          <CardTitle>Get started with Werkio Beta</CardTitle>
          <CardDescription>
            This is a real Werkio workspace, not demo data. You can start blank and import your
            workers later.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ol className="flex flex-wrap justify-center gap-2 text-xs sm:gap-3">
            {STEPS.map((item) => {
              const Icon = item.icon;
              const active = item.id === step;
              const done = item.id < step;
              return (
                <li
                  key={item.id}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 ${
                    active
                      ? "border-primary bg-primary/10 text-primary"
                      : done
                        ? "border-green-600/30 bg-green-50 text-green-700"
                        : "border-border text-muted-foreground"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="font-medium">{item.title}</span>
                </li>
              );
            })}
          </ol>

          {step === 1 ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company name</Label>
                <Input
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Construction"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <select
                  id="timezone"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  {TIMEZONE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="currency">Default currency</Label>
                  <select
                    id="currency"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={defaultCurrency}
                    onChange={(e) =>
                      setDefaultCurrency(e.target.value as "USD" | "CAD" | "EUR")
                    }
                  >
                    {BETA_CURRENCY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payPeriod">Pay period type</Label>
                  <select
                    id="payPeriod"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={payPeriodType}
                    onChange={(e) =>
                      setPayPeriodType(
                        e.target.value as "WEEKLY" | "BIWEEKLY" | "SEMIMONTHLY" | "MONTHLY",
                      )
                    }
                  >
                    {BETA_PAY_PERIOD_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="space-y-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={skipSite}
                  onChange={(e) => setSkipSite(e.target.checked)}
                />
                Skip for now — I&apos;ll add sites later
              </label>
              {!skipSite ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="siteName">Site name</Label>
                    <Input
                      id="siteName"
                      value={siteName}
                      onChange={(e) => {
                        setSiteName(e.target.value);
                        if (!siteCode) setSiteCode(slugifySiteCode(e.target.value));
                      }}
                      placeholder="Northgate Tower"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="siteCode">Site code</Label>
                    <Input
                      id="siteCode"
                      value={siteCode}
                      onChange={(e) => setSiteCode(e.target.value.toUpperCase())}
                      placeholder="NORTHGATE"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="siteAddress">Address</Label>
                    <Input
                      id="siteAddress"
                      value={siteAddress}
                      onChange={(e) => setSiteAddress(e.target.value)}
                      placeholder="123 Main St, City, ST"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={geofenceEnabled}
                      onChange={(e) => setGeofenceEnabled(e.target.checked)}
                    />
                    Enable geofencing
                  </label>
                  {geofenceEnabled ? (
                    <div className="space-y-2">
                      <Label htmlFor="geofenceRadius">Geofence radius (meters)</Label>
                      <Input
                        id="geofenceRadius"
                        type="number"
                        min={10}
                        max={50000}
                        value={geofenceRadiusMeters}
                        onChange={(e) => setGeofenceRadiusMeters(Number(e.target.value))}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}

          {step === 3 ? (
            <div className="grid gap-3">
              {SETUP_PATH_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`rounded-lg border p-4 text-left transition-colors ${
                    setupPath === option.value
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/40"
                  }`}
                  onClick={() => setSetupPath(option.value)}
                >
                  <p className="font-medium">{option.label}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{option.description}</p>
                </button>
              ))}
            </div>
          ) : null}

          {step === 4 ? (
            <div className="grid gap-3">
              {PAY_RULE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`rounded-lg border p-4 text-left transition-colors ${
                    payRuleTemplate === option.value
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/40"
                  }`}
                  onClick={() => setPayRuleTemplate(option.value)}
                >
                  <p className="font-medium">{option.label}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{option.description}</p>
                </button>
              ))}
            </div>
          ) : null}

          {step === 5 ? (
            <div className="space-y-3 rounded-lg border bg-card p-4 text-sm">
              <p>
                <span className="font-medium">Company:</span> {companyName}
              </p>
              <p>
                <span className="font-medium">Timezone:</span> {timezone}
              </p>
              <p>
                <span className="font-medium">Currency / pay period:</span> {defaultCurrency} ·{" "}
                {BETA_PAY_PERIOD_OPTIONS.find((o) => o.value === payPeriodType)?.label}
              </p>
              <p>
                <span className="font-medium">First site:</span>{" "}
                {skipSite ? "Skipped" : siteName || "—"}
              </p>
              <p>
                <span className="font-medium">Worker setup:</span>{" "}
                {BETA_SETUP_PATH_LABELS[setupPath]}
              </p>
              <p>
                <span className="font-medium">Pay rules:</span>{" "}
                {payRuleTemplate === "later"
                  ? "Configure later"
                  : BETA_PAY_RULE_TEMPLATE_LABELS[payRuleTemplate]}
              </p>
              <InfoBanner variant="info">
                We&apos;ll create your workspace with no demo workers, equipment, time entries, or
                pay periods.
              </InfoBanner>
            </div>
          ) : null}

          {error ? (
            <InfoBanner variant="error" title="Could not continue">
              {error}
            </InfoBanner>
          ) : null}

          <div className="flex gap-3">
            {step > 1 ? (
              <Button type="button" variant="outline" onClick={goBack} disabled={submitting}>
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back
              </Button>
            ) : (
              <div />
            )}
            {step < STEPS.length ? (
              <Button type="button" className="ml-auto" onClick={goNext}>
                Next
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                className="ml-auto"
                disabled={submitting}
                onClick={() => void handleFinish()}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating workspace…
                  </>
                ) : (
                  "Finish and open workspace"
                )}
              </Button>
            )}
          </div>

          <p className="text-center text-sm text-muted-foreground">
            <Link to="/demo" className="font-medium text-orange-construction hover:underline">
              Try the demo
            </Link>
            {" · "}
            <Link to="/" className="font-medium hover:underline">
              Home
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
